import { CSSProperties, useLayoutEffect, useRef } from "react";

import { getGridCellBorderEdges } from "@/components/organisms/Table/Table.tools";
import { useTableStore } from "@/components/organisms/Table/Table.store";
import { Values } from "@/constants";
import { getTableRowClassName } from "./TableRow";
import { Checkbox } from "@/components/ui/checkbox";
import { typographyStyles } from "@/components/ui/typography";

function TableRowCheckbox<RowType extends { id: string }>({
  index,
  rowId,
  loading,
  contextId,
  hideOuterBorders = false,
  isLastRow = false,
}: {
  index: number;
  rowId: RowType["id"];
  loading: boolean;
  contextId: string;
  hideOuterBorders?: boolean;
  isLastRow?: boolean;
}) {
  const toggleRowCheck = useTableStore((state) => state.toggleIsRowChecked);
  const isChecked = useTableStore((store) => store.checkedRows[rowId] === true);
  const borderEdges = getGridCellBorderEdges({
    color: "var(--border)",
    isOuterBottom: isLastRow,
    hideOuterEdges: hideOuterBorders,
  });
  const checkboxRef = useRef<HTMLLabelElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (isChecked) {
      if (labelRef.current) {
        labelRef.current.style.display = "none";
      }
      if (checkboxRef.current) {
        checkboxRef.current.style.display = "flex";
      }
    } else {
      if (labelRef.current) {
        labelRef.current.style.display = "flex";
      }
      if (checkboxRef.current) {
        checkboxRef.current.style.display = "none";
      }
    }
  }, [isChecked]);

  return (
    <>
      <div
        className={getTableRowClassName(contextId, index, "")}
        data-grid-cell
        onClick={(e) => {
          e.stopPropagation();
        }}
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          position: "relative",
          display: "flex",
          minWidth: Values.DEFAULT_ROW_HEIGHT + "px",
          maxWidth: Values.DEFAULT_ROW_HEIGHT + "px",
          height: Values.DEFAULT_ROW_HEIGHT + "px",
          minHeight: Values.DEFAULT_ROW_HEIGHT + "px",
          maxHeight: Values.DEFAULT_ROW_HEIGHT + "px",
          alignItems: "center",
          justifyContent: "center",
          ...borderEdges,
          ...(typographyStyles["body-md"] as CSSProperties),
          backgroundColor: "var(--grid-row-bg)",
        }}
      >
        {/* The cell is its tick's label: pressed anywhere in it, the tick
            in its middle is - at the tick's own size. */}
        <label
          ref={checkboxRef}
          className={getTableRowClassName(contextId, index, "checkbox")}
          style={{
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            display: "none",
            cursor: "pointer",
          }}
        >
          <Checkbox
            checked={isChecked}
            disabled={loading}
            onCheckedChange={() => {
              toggleRowCheck(rowId);
            }}
          />
        </label>
        <div
          ref={labelRef}
          className={getTableRowClassName(contextId, index, "checkbox-label")}
          style={{
            ...(typographyStyles["body-xs"] as CSSProperties),
            color:
              "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
            display: "flex",
          }}
        >
          {index + 1}
        </div>
      </div>
    </>
  );
}

export { TableRowCheckbox };
